import { CLUSTER_TYPE } from '@/types/enums';
import type { ChurchListedItem } from '@/types/churchListedItem.type';
import {
  PARISH_DETAILS_CONTEXT_MODE,
  PARISH_DETAILS_QUERY_PARAM,
  PARISH_DETAILS_TAB_NAME,
} from '@/routing/routesAliases';

export const formatParishListedAddress = ({
  parish,
}: {
  parish: Pick<ChurchListedItem, 'address1' | 'city' | 'state'>;
}): string => {
  return [parish.address1, parish.city, parish.state].filter(Boolean).join(', ');
};

export const isLimitedClusterSubChurch = ({
  parish,
  clusterType,
}: {
  parish: ChurchListedItem;
  clusterType?: CLUSTER_TYPE | null;
}): boolean => {
  if (parish.isMain) {
    return false;
  }

  if (typeof parish.hasFullProductsEnabled === 'boolean') {
    return !parish.hasFullProductsEnabled;
  }

  return clusterType === CLUSTER_TYPE.ONE;
};

export const buildDotMenuScopeVisibility = ({
  clusterItems,
}: {
  clusterItems: ChurchListedItem[];
}): ((churchId: string) => boolean) | null => {
  const hasDotMenuFilter = clusterItems.some((item) => typeof item.isAccessibleFromDotMenu === 'boolean');

  if (!hasDotMenuFilter) {
    return null;
  }

  const churchAccessibility = new Map<string, boolean>();

  clusterItems.forEach((item) => {
    if (typeof item.isAccessibleFromDotMenu === 'boolean') {
      churchAccessibility.set(item.id, item.isAccessibleFromDotMenu);
    }
  });

  return (churchId: string) => {
    const accessibility = churchAccessibility.get(churchId);

    if (accessibility === undefined) {
      return true;
    }

    return accessibility;
  };
};

export const appendLimitedClusterSubQueryToPath = ({ path, search }: { path: string; search: string }): string => {
  const queryParams = new URLSearchParams(search);

  if (queryParams.get(PARISH_DETAILS_QUERY_PARAM.CONTEXT_MODE) !== PARISH_DETAILS_CONTEXT_MODE.CLUSTER_ONE) {
    return path;
  }

  const clusterQuery = new URLSearchParams();

  clusterQuery.set(PARISH_DETAILS_QUERY_PARAM.CONTEXT_MODE, PARISH_DETAILS_CONTEXT_MODE.CLUSTER_ONE);

  const clusteredChurchId = queryParams.get(PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_ID);
  const clusteredChurchName = queryParams.get(PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_NAME);

  if (clusteredChurchId) {
    clusterQuery.set(PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_ID, clusteredChurchId);
  }

  if (clusteredChurchName) {
    clusterQuery.set(PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_NAME, clusteredChurchName);
  }

  const queryString = clusterQuery.toString();

  return queryString ? `${path}?${queryString}` : path;
};

export const getLimitedClusterSubQuery = ({ parish }: { parish: ChurchListedItem }) => ({
  [PARISH_DETAILS_QUERY_PARAM.CONTEXT_MODE]: PARISH_DETAILS_CONTEXT_MODE.CLUSTER_ONE,
  [PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_ID]: parish.id,
  [PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_NAME]: parish.name,
});

export const getLimitedClusterSubQueryFromSearch = ({
  search,
}: {
  search: string;
}): Partial<Record<PARISH_DETAILS_QUERY_PARAM, string>> | undefined => {
  const queryParams = new URLSearchParams(search);

  if (queryParams.get(PARISH_DETAILS_QUERY_PARAM.CONTEXT_MODE) !== PARISH_DETAILS_CONTEXT_MODE.CLUSTER_ONE) {
    return undefined;
  }

  const query: Partial<Record<PARISH_DETAILS_QUERY_PARAM, string>> = {
    [PARISH_DETAILS_QUERY_PARAM.CONTEXT_MODE]: PARISH_DETAILS_CONTEXT_MODE.CLUSTER_ONE,
  };

  const clusteredChurchId = queryParams.get(PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_ID);
  const clusteredChurchName = queryParams.get(PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_NAME);

  if (clusteredChurchId) {
    query[PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_ID] = clusteredChurchId;
  }

  if (clusteredChurchName) {
    query[PARISH_DETAILS_QUERY_PARAM.CLUSTERED_CHURCH_NAME] = clusteredChurchName;
  }

  return query;
};

export const LIMITED_CLUSTER_SUB_NAV_KEYS = ['parish-info'];

export const LIMITED_CLUSTER_SUB_TABS = [
  PARISH_DETAILS_TAB_NAME.INFO,
  PARISH_DETAILS_TAB_NAME.DIRECTORY,
  PARISH_DETAILS_TAB_NAME.MASS_TIME,
  PARISH_DETAILS_TAB_NAME.CUSTOMIZATION_FIELDS,
];
